import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FeeBasedService, ServiceCost } from "@vp/models";
import { FeatureConfig, FeatureService } from "@vp/shared/features";
import { filterNullMap } from "@vp/shared/operators";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

const FEE_BASED_SERVICE_FEATURE_ID = "feeBasedServices";

export interface DialogData {
  mode: "add" | "edit";
  [key: string]: any;
}

@Component({
  selector: "vp-add-service-fee-override-dialog",
  templateUrl: "./add-service-fee-override-dialog.component.html",
  styleUrls: ["./add-service-fee-override-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddServiceFeeOverrideDialogComponent {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<AddServiceFeeOverrideDialogComponent>>(MatDialogRef);
  readonly formBuilder = inject(UntypedFormBuilder);
  private readonly featureService = inject(FeatureService);

  formGroup!: UntypedFormGroup;

  constructor() {
    const data = this.data;
    const formBuilder = this.formBuilder;

    // form output should match ServiceCost interface
    this.formGroup = formBuilder.group({
      friendlyId: [data.friendlyId ?? null, Validators.required],
      fee: [data.fee ?? null, Validators.required],
      active: [data.active ?? false, Validators.required]
    });

    if (this.data.mode === "edit") {
      this.formGroup.get("friendlyId")?.disable();
    }
  }

  feature$: Observable<FeatureConfig | null> = this.featureService.feature$(
    FEE_BASED_SERVICE_FEATURE_ID
  );

  allFeeBasedServices$ = this.feature$.pipe(
    filterNullMap(),
    map(featureConfig => {
      if (featureConfig?.feeBasedServices && this.data.existingServices) {
        return (<FeeBasedService[]>featureConfig?.feeBasedServices).filter(feeService =>
          (<ServiceCost[]>this.data.existingServices).every(
            s => s.friendlyId !== feeService.friendlyId
          )
        );
      }
      return featureConfig?.feeBasedServices ?? [];
    })
  );

  onCancel = () => {
    this.close(false);
  };

  onSave = () => {
    this.close(true);
  };

  close(save: boolean) {
    if (save && this.formGroup.valid) {
      this.dialogRef.close(this.formGroup.getRawValue());
    } else {
      if (this.formGroup.pristine || this.formGroup.invalid) {
        this.dialogRef.close();
      } else {
        if (confirm("Are you sure you want to cancel and potentially lose data?")) {
          this.dialogRef.close();
        }
      }
    }
  }
}
